import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  BoltIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesSolidIcon } from '@heroicons/react/24/solid';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  getBlogAuthorsWithStats,
  generateBlogAuthor,
  deleteBlogAuthor,
  toggleBlogAuthorActive,
  analyzeAuthorReassignments,
  executeAuthorReassignments,
  type AnalyzeReassignmentResponse,
} from '@/services/blogApi';
import type { BlogAuthorWithStats } from '@/types/blog';

export default function BlogAuthors() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedAuthor, setSelectedAuthor] = useState<BlogAuthorWithStats | null>(null);

  // Generate author form
  const [expertise, setExpertise] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');

  // Reassignment state
  const [reassignAnalysis, setReassignAnalysis] = useState<AnalyzeReassignmentResponse | null>(null);
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  // Fetch authors
  const { data: authorsData, isLoading } = useQuery({
    queryKey: ['blogAuthors', page, search, activeFilter],
    queryFn: () =>
      getBlogAuthorsWithStats(
        page,
        20,
        search || undefined,
        activeFilter === 'all' ? undefined : activeFilter === 'active'
      ),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteBlogAuthor(id),
    onSuccess: () => {
      toast.success('Author deleted');
      queryClient.invalidateQueries({ queryKey: ['blogAuthors'] });
      setShowDeleteModal(false);
      setSelectedAuthor(null);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to delete author';
      toast.error(message);
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      toggleBlogAuthorActive(id, isActive),
    onSuccess: (_, { isActive }) => {
      toast.success(`Author ${isActive ? 'activated' : 'deactivated'}`);
      queryClient.invalidateQueries({ queryKey: ['blogAuthors'] });
    },
    onError: () => toast.error('Failed to update author status'),
  });

  // Generate author mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!expertise.trim()) {
        throw new Error('Expertise is required');
      }
      return generateBlogAuthor({
        expertise: expertise.trim(),
        additional_context: additionalContext.trim() || undefined,
      });
    },
    onSuccess: (data) => {
      if (data.success && data.author) {
        toast.success(`Created author: ${data.author.name}`);
        queryClient.invalidateQueries({ queryKey: ['blogAuthors'] });
        setShowGenerateModal(false);
        setExpertise('');
        setAdditionalContext('');
        // Navigate to the new author's detail page
        navigate(`/blog-authors/${data.author.id}`);
      } else {
        toast.error(data.error || 'Failed to generate author');
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to generate author';
      toast.error(message);
    },
  });

  // Analyze reassignment
  const handleAnalyzeReassignments = async () => {
    setIsAnalyzing(true);
    setReassignAnalysis(null);
    try {
      const result = await analyzeAuthorReassignments(unassignedOnly);
      setReassignAnalysis(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to analyze';
      toast.error(message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Execute reassignment
  const handleExecuteReassignments = async () => {
    setIsReassigning(true);
    try {
      const result = await executeAuthorReassignments(unassignedOnly, false);
      if (result.success) {
        toast.success(`Updated ${result.posts_updated} posts`);
        queryClient.invalidateQueries({ queryKey: ['blogAuthors'] });
        setShowReassignModal(false);
        setReassignAnalysis(null);
      } else {
        toast.error('Failed to reassign authors');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reassign';
      toast.error(message);
    } finally {
      setIsReassigning(false);
    }
  };

  const columns = useMemo<ColumnDef<BlogAuthorWithStats>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Author',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {row.original.avatar_url ? (
              <img
                src={row.original.avatar_url}
                alt={row.original.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
                <span className="text-brand font-medium text-sm">
                  {row.original.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </span>
              </div>
            )}
            <div>
              <button
                type="button"
                onClick={() => navigate(`/blog-authors/${row.original.id}`)}
                className="font-medium text-gray-900 hover:text-brand hover:underline text-left"
              >
                {row.original.name}
              </button>
              <div className="text-sm text-gray-500 flex items-center gap-1">
                {row.original.ai_generated && (
                  <SparklesSolidIcon className="w-3 h-3 text-purple-500" title="AI Generated" />
                )}
                <span className="truncate max-w-xs">{row.original.id}</span>
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'expertise',
        header: 'Expertise',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1 max-w-xs">
            {(row.original.expertise || []).slice(0, 3).map((exp) => (
              <Badge key={exp} variant="gray" size="sm">
                {exp.replace(/-/g, ' ')}
              </Badge>
            ))}
            {(row.original.expertise || []).length > 3 && (
              <Badge variant="gray" size="sm">
                +{(row.original.expertise || []).length - 3}
              </Badge>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'total_posts',
        header: 'Posts',
        cell: ({ row }) => (
          <div className="text-sm">
            <span className="font-medium">{row.original.total_posts}</span>
            <span className="text-gray-500 ml-1">
              ({row.original.published_posts} published)
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'total_views',
        header: 'Total Views',
        cell: ({ row }) => (
          <span className="text-sm font-medium">
            {row.original.total_views.toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? 'success' : 'gray'} dot>
            {row.original.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                navigate(`/blog-authors/${row.original.id}`);
              }}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                toggleActiveMutation.mutate({
                  id: row.original.id,
                  isActive: !row.original.is_active,
                });
              }}
              className={`p-2 hover:bg-gray-100 rounded ${
                row.original.is_active
                  ? 'text-gray-400 hover:text-orange-600'
                  : 'text-gray-400 hover:text-green-600'
              }`}
              title={row.original.is_active ? 'Deactivate' : 'Activate'}
            >
              {row.original.is_active ? (
                <EyeSlashIcon className="w-4 h-4" />
              ) : (
                <EyeIcon className="w-4 h-4" />
              )}
            </button>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setSelectedAuthor(row.original);
                setShowDeleteModal(true);
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
              title="Delete"
              disabled={row.original.total_posts > 0}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [navigate, toggleActiveMutation]
  );

  // Calculate stats
  const totalAuthors = authorsData?.total || 0;
  const activeAuthors = authorsData?.authors.filter(a => a.is_active).length || 0;
  const aiGeneratedAuthors = authorsData?.authors.filter(a => a.ai_generated).length || 0;
  const totalPosts = authorsData?.authors.reduce((sum, a) => sum + a.total_posts, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog Authors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage blog author personas and assign them to posts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setShowReassignModal(true);
              setReassignAnalysis(null);
            }}
            leftIcon={<BoltIcon className="w-4 h-4" />}
          >
            Auto-Assign Authors
          </Button>
          <Button
            onClick={() => setShowGenerateModal(true)}
            leftIcon={<SparklesIcon className="w-4 h-4" />}
          >
            Generate Author
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total Authors</p>
            <p className="text-2xl font-bold">{totalAuthors}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-600">{activeAuthors}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">AI Generated</p>
            <p className="text-2xl font-bold text-purple-600">{aiGeneratedAuthors}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total Posts</p>
            <p className="text-2xl font-bold text-blue-600">{totalPosts}</p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <select
            value={activeFilter}
            onChange={(e) => {
              setActiveFilter(e.target.value as 'all' | 'active' | 'inactive');
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="all">All Authors</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>

        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="authors-search"
            name="authorsSearch"
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search authors..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
            aria-label="Search authors"
          />
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={authorsData?.authors || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={authorsData?.total}
            onPageChange={setPage}
            getRowId={(row) => row.id}
          />
        </CardBody>
      </Card>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedAuthor(null);
        }}
        title="Delete Author"
      >
        {selectedAuthor?.total_posts && selectedAuthor.total_posts > 0 ? (
          <div className="space-y-4">
            <p className="text-gray-600">
              Cannot delete "{selectedAuthor.name}" because they have{' '}
              <strong>{selectedAuthor.total_posts} blog posts</strong> assigned.
            </p>
            <p className="text-sm text-gray-500">
              Please reassign their posts to another author first, or deactivate
              this author instead.
            </p>
          </div>
        ) : (
          <p className="text-gray-600">
            Are you sure you want to delete "{selectedAuthor?.name}"? This action
            cannot be undone.
          </p>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          {selectedAuthor && (!selectedAuthor.total_posts || selectedAuthor.total_posts === 0) && (
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate(selectedAuthor.id)}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Generate Author Modal */}
      <Modal
        isOpen={showGenerateModal}
        onClose={() => {
          if (!generateMutation.isPending) {
            setShowGenerateModal(false);
            setExpertise('');
            setAdditionalContext('');
          }
        }}
        title="Generate New Author"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Enter an area of expertise and our AI will generate a professional
            author persona with a name and bio.
          </p>

          <div>
            <label htmlFor="generate-author-expertise" className="block text-sm font-medium text-gray-700 mb-1">
              Area of Expertise *
            </label>
            <input
              id="generate-author-expertise"
              name="expertise"
              type="text"
              value={expertise}
              onChange={(e) => setExpertise(e.target.value)}
              placeholder="e.g., plumbing, automotive, home renovation"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              disabled={generateMutation.isPending}
              aria-label="Area of expertise"
            />
            <p className="mt-1 text-xs text-gray-500">
              You can enter multiple areas separated by commas
            </p>
          </div>

          <div>
            <label htmlFor="generate-author-context" className="block text-sm font-medium text-gray-700 mb-1">
              Additional Context (Optional)
            </label>
            <textarea
              id="generate-author-context"
              name="additionalContext"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="e.g., Should have experience in commercial projects, or focus on budget-friendly solutions"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              disabled={generateMutation.isPending}
              aria-label="Additional context"
            />
          </div>

          {generateMutation.isPending && (
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <ArrowPathIcon className="w-5 h-5 text-purple-600 animate-spin" />
              <span className="text-purple-700">Generating author persona...</span>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowGenerateModal(false);
              setExpertise('');
              setAdditionalContext('');
            }}
            disabled={generateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => generateMutation.mutate()}
            loading={generateMutation.isPending}
            disabled={!expertise.trim()}
            leftIcon={<SparklesIcon className="w-4 h-4" />}
          >
            Generate
          </Button>
        </ModalFooter>
      </Modal>

      {/* Auto-Assign Authors Modal */}
      <Modal
        isOpen={showReassignModal}
        onClose={() => {
          if (!isAnalyzing && !isReassigning) {
            setShowReassignModal(false);
            setReassignAnalysis(null);
          }
        }}
        title="Auto-Assign Blog Authors"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Automatically assign the best-matching author to blog posts based on
            their category and subcategory. Authors are matched by their expertise areas.
          </p>

          {!reassignAnalysis && (
            <>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="unassignedOnly"
                  name="unassignedOnly"
                  checked={unassignedOnly}
                  onChange={(e) => setUnassignedOnly(e.target.checked)}
                  className="rounded border-gray-300"
                  disabled={isAnalyzing}
                  aria-label="Only process posts without an assigned author"
                />
                <label htmlFor="unassignedOnly" className="text-sm text-gray-700">
                  Only process posts without an assigned author
                </label>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  Click "Analyze" to see which posts will be affected before making changes.
                </p>
              </div>
            </>
          )}

          {isAnalyzing && (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <ArrowPathIcon className="w-5 h-5 text-brand animate-spin" />
              <span className="text-gray-700">Analyzing posts...</span>
            </div>
          )}

          {reassignAnalysis && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {reassignAnalysis.total_posts}
                  </p>
                  <p className="text-sm text-gray-500">Total Posts</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-600">
                    {reassignAnalysis.posts_without_author}
                  </p>
                  <p className="text-sm text-gray-500">Without Author</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {reassignAnalysis.recommendations.length}
                  </p>
                  <p className="text-sm text-gray-500">Will Change</p>
                </div>
              </div>

              {/* Recommendations Preview */}
              {reassignAnalysis.recommendations.length > 0 && (
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Post</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Category</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">New Author</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {reassignAnalysis.recommendations.slice(0, 50).map((rec) => (
                        <tr key={rec.post_id} className={rec.current_author_id ? 'bg-yellow-50' : ''}>
                          <td className="px-4 py-2 text-sm text-gray-900 truncate max-w-xs" title={rec.title}>
                            {rec.title.length > 40 ? rec.title.slice(0, 40) + '...' : rec.title}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {rec.category_slug}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span className="font-medium text-green-700">
                              {rec.recommended_author_name}
                            </span>
                            {rec.current_author_name && (
                              <span className="text-gray-400 text-xs ml-1">
                                (was: {rec.current_author_name})
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <Badge variant={rec.match_score >= 50 ? 'success' : rec.match_score >= 25 ? 'warning' : 'gray'}>
                              {rec.match_score}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                      {reassignAnalysis.recommendations.length > 50 && (
                        <tr>
                          <td colSpan={4} className="px-4 py-2 text-sm text-gray-500 italic text-center">
                            ...and {reassignAnalysis.recommendations.length - 50} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {reassignAnalysis.recommendations.length === 0 && (
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  <span className="text-green-700">
                    All posts already have optimal author assignments!
                  </span>
                </div>
              )}
            </div>
          )}

          {isReassigning && (
            <div className="flex items-center gap-3 p-4 bg-brand/10 rounded-lg">
              <ArrowPathIcon className="w-5 h-5 text-brand animate-spin" />
              <span className="text-brand">Reassigning authors...</span>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowReassignModal(false);
              setReassignAnalysis(null);
            }}
            disabled={isAnalyzing || isReassigning}
          >
            {reassignAnalysis ? 'Close' : 'Cancel'}
          </Button>
          {!reassignAnalysis ? (
            <Button
              onClick={handleAnalyzeReassignments}
              loading={isAnalyzing}
              leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
            >
              Analyze
            </Button>
          ) : reassignAnalysis.recommendations.length > 0 ? (
            <Button
              onClick={handleExecuteReassignments}
              loading={isReassigning}
              leftIcon={<BoltIcon className="w-4 h-4" />}
            >
              Reassign {reassignAnalysis.recommendations.length} Posts
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    </div>
  );
}
